// src/pages/WerkbonOplevering.js
// vWB-014
// - Eerste eenvoudig Werkbon / Oplevering-scherm
// - Eén werkbon per opdracht
// - Relatie, contactpersoon, bedrijfscode en opdrachtregels automatisch overgenomen
// - Opslaan via /api/offers/:id/work-order
// - Werkzaamheden kunnen per werkbon worden aangepast zonder de oorspronkelijke opdrachtregels te wijzigen
// - Werkregels sluiten visueel aan op Offertes: potlood, prullenbak, omhoog/omlaag en slepen

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const VERSION = "vWB-014";

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function isoDate(value) {
  const text = safeText(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

function buildHeaders() {
  let user = {};
  try {
    user = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
  } catch {}

  const headers = {
    "Content-Type": "application/json",
    "x-tenant":
      sessionStorage.getItem("tenant") ||
      localStorage.getItem("tenant") ||
      "demo",
    "x-role": user?.role || "user",
    "x-staffiq-user-id": user?.id || "",
    "x-staffiq-user-email": user?.email || "",
    "x-staffiq-user-name": user?.display_name || user?.name || "",
    "x-company-code-ids":
      sessionStorage.getItem("staffiq_company_code_ids") ||
      localStorage.getItem("staffiq_company_code_ids") ||
      "",
  };

  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  if (token) headers.Authorization = `Bearer ${token}`;

  Object.keys(headers).forEach((key) => {
    if (!headers[key]) delete headers[key];
  });

  return headers;
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...buildHeaders(),
      ...(options.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.error || `${response.status} ${response.statusText}`);
  }

  return data;
}

const emptyForm = {
  execution_date: "",
  start_time: "",
  executed_by: "",
  customer_access: "",
  key_returned: false,
  maintenance_instruction: "",
  photos_required: false,
  photos_received: false,
  extra_work: "",
  completion_notes: "",
  approved_name: "",
  completion_date: "",
  completion_status: "NOT_COMPLETED",
  active: true,
};

function statusLabel(value) {
  const status = safeText(value).toUpperCase();
  if (status === "PENDING_APPROVAL") return "Ter akkoord";
  if (status === "COMPLETED") return "Opgeleverd";
  return "Niet opgeleverd";
}

function escapeHtml(value) {
  return safeText(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function nlDate(value) {
  const date = isoDate(value);
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
}

function yesNo(value) {
  return value === true ? "Ja" : "Nee";
}

export default function WerkbonOplevering() {
  const { offerId } = useParams();
  const navigate = useNavigate();

  const [source, setSource] = useState(null);
  const [offerLines, setOfferLines] = useState([]);
  const [workLines, setWorkLines] = useState([]);
  const [editingWorkLineIndex, setEditingWorkLineIndex] = useState(null);
  const [workLineDraft, setWorkLineDraft] = useState({
    qty: 1,
    description: "",
  });
  const [draggingWorkLineIndex, setDraggingWorkLineIndex] = useState(null);
  const [dragOverWorkLineIndex, setDragOverWorkLineIndex] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState({});
  const [workOrderSettings, setWorkOrderSettings] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingApproval, setSendingApproval] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const aiTextRef = useRef(null);
  const previewFrameRef = useRef(null);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  let role = "user";
  try {
    const user = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
    role = safeText(user?.role, "user").toLowerCase();
  } catch {}

  const isReadonly = role === "readonly";

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const [workOrderData, offerData, settingsData] = await Promise.all([
          fetchJson(`/api/offers/${encodeURIComponent(offerId)}/work-order`),
          fetchJson(`/api/offers/${encodeURIComponent(offerId)}`),
          fetchJson("/api/invoice-settings").catch(() => ({})),
        ]);

        if (cancelled) return;

        const loadedSource = workOrderData?.source || null;
        setSource(loadedSource);
        const companyCodeId = loadedSource?.company_code_id || "";
        const workOrderSettingsData = await fetchJson(
          `/api/company-section-settings/mkb_work_orders${companyCodeId ? `?company_code_id=${encodeURIComponent(companyCodeId)}` : ""}`
        ).catch(() => ({}));
        if (cancelled) return;
        setWorkOrderSettings(workOrderSettingsData?.settings || {});
        setInvoiceSettings(
          settingsData && typeof settingsData === "object"
            ? settingsData
            : {}
        );

        const workOrder = workOrderData?.work_order;
        if (workOrder) {
          setForm({
            ...emptyForm,
            ...workOrder,
            execution_date: isoDate(workOrder.execution_date),
            completion_date: isoDate(workOrder.completion_date),
            start_time: safeText(workOrder.start_time),
            key_returned: workOrder.key_returned === true,
            photos_required: workOrder.photos_required === true,
            photos_received: workOrder.photos_received === true,
            active: workOrder.active !== false,
          });
        } else {
          setForm(emptyForm);
        }

        const lines =
          Array.isArray(offerData?.items) ? offerData.items :
          Array.isArray(offerData?.lines) ? offerData.lines :
          [];

        const savedWorkLines = Array.isArray(workOrder?.work_lines)
          ? workOrder.work_lines
          : [];

        const initialWorkLines = savedWorkLines.length
          ? savedWorkLines
          : lines.map((line, index) => ({
              id: line?.id || `offer-${index}`,
              source_line_id: line?.id || null,
              qty: Number(line?.qty ?? line?.quantity ?? 0) || 0,
              description: safeText(line?.description),
            }));

        setOfferLines(lines);
        setWorkLines(initialWorkLines);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Werkbon laden mislukt.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [offerId]);

  const visibleLines = useMemo(() => {
    return (workLines || []).filter((line) => {
      const description = safeText(line?.description).trim();
      return !!description;
    });
  }, [workLines]);

  function startEditWorkLine(index) {
    const line = workLines[index];
    if (!line) return;

    setEditingWorkLineIndex(index);
    setWorkLineDraft({
      qty: line.qty ?? 0,
      description: safeText(line.description),
    });
  }

  function cancelEditWorkLine() {
    setEditingWorkLineIndex(null);
    setWorkLineDraft({
      qty: 1,
      description: "",
    });
  }

  function saveEditedWorkLine() {
    if (editingWorkLineIndex === null) return;

    const description = safeText(workLineDraft.description).trim();
    if (!description) {
      setError("Omschrijving werkzaamheden is verplicht.");
      return;
    }

    setWorkLines((previous) =>
      previous.map((line, lineIndex) =>
        lineIndex === editingWorkLineIndex
          ? {
              ...line,
              qty: Number(workLineDraft.qty || 0) || 0,
              description,
            }
          : line
      )
    );

    setEditingWorkLineIndex(null);
    setWorkLineDraft({
      qty: 1,
      description: "",
    });
    setError("");
  }

  function removeWorkLine(index) {
    if (!window.confirm("Werkregel verwijderen?")) return;

    setWorkLines((previous) =>
      previous.filter((_, lineIndex) => lineIndex !== index)
    );

    if (editingWorkLineIndex === index) {
      cancelEditWorkLine();
    } else if (
      editingWorkLineIndex !== null &&
      index < editingWorkLineIndex
    ) {
      setEditingWorkLineIndex(editingWorkLineIndex - 1);
    }
  }

  function addWorkLine() {
    const nextLine = {
      id: `new-${Date.now()}`,
      source_line_id: null,
      qty: 1,
      description: "",
    };

    setWorkLines((previous) => {
      const next = [...previous, nextLine];

      setTimeout(() => {
        setEditingWorkLineIndex(next.length - 1);
        setWorkLineDraft({
          qty: 1,
          description: "",
        });
      }, 0);

      return next;
    });
  }

  function moveWorkLineStep(index, direction) {
    const targetIndex = index + Number(direction || 0);
    if (
      index < 0 ||
      targetIndex < 0 ||
      index >= workLines.length ||
      targetIndex >= workLines.length
    ) {
      return;
    }

    setWorkLines((previous) => {
      const next = previous.slice();
      const [moved] = next.splice(index, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });

    if (editingWorkLineIndex === index) {
      setEditingWorkLineIndex(targetIndex);
    } else if (editingWorkLineIndex === targetIndex) {
      setEditingWorkLineIndex(index);
    }
  }

  function reorderWorkLine(fromIndex, toIndex) {
    if (
      fromIndex === null ||
      toIndex === null ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= workLines.length ||
      toIndex >= workLines.length
    ) {
      return;
    }

    setWorkLines((previous) => {
      const next = previous.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });

    if (editingWorkLineIndex === fromIndex) {
      setEditingWorkLineIndex(toIndex);
    }

    setDraggingWorkLineIndex(null);
    setDragOverWorkLineIndex(null);
  }

  function resetWorkLinesFromOffer() {
    const resetLines = (offerLines || []).map((line, index) => ({
      id: line?.id || `offer-${index}`,
      source_line_id: line?.id || null,
      qty: Number(line?.qty ?? line?.quantity ?? 0) || 0,
      description: safeText(line?.description),
    }));

    setWorkLines(resetLines);
    cancelEditWorkLine();
  }

  function updateField(name, value) {
    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  }

  function workOrderPayload() {
    return {
      execution_date: form.execution_date || null,
      start_time: form.start_time || null,
      executed_by: form.executed_by || "",
      customer_access: form.customer_access || "",
      key_returned: form.key_returned === true,
      maintenance_instruction: form.maintenance_instruction || "",
      photos_required: form.photos_required === true,
      photos_received: form.photos_received === true,
      extra_work: form.extra_work || "",
      completion_notes: form.completion_notes || "",
      approved_name: form.approved_name || "",
      completion_date: form.completion_date || null,
      completion_status: form.completion_status || "NOT_COMPLETED",
      work_lines: (workLines || []).map((line, index) => ({
        id: line?.id || `line-${index}`,
        source_line_id: line?.source_line_id || null,
        qty: Number(line?.qty || 0) || 0,
        description: safeText(line?.description),
      })),
      active: true,
    };
  }

  function buildWorkOrderHtml() {
    const setting = (key, fallback = "") => {
      const value = safeText(workOrderSettings?.[key]).trim();
      return value || fallback;
    };
    const logo = setting("logo_url", safeText(invoiceSettings?.logo_url).trim());
    const companyName = setting(
      "company_name",
      safeText(invoiceSettings?.offer_company_name).trim() ||
        safeText(invoiceSettings?.company_name).trim() ||
        "StaffIQ"
    );
    const documentTitle = setting("document_title", "Werkbon / Oplevering");
    const executionTitle = setting("execution_title", "Uitvoering");
    const completionTitle = setting("completion_title", "Oplevering");
    const workTitle = setting("work_title", "Opdrachtwerkzaamheden");
    const extraWorkTitle = setting("extra_work_title", "Extra materialen / werkzaamheden");
    const approvalBlockText = setting("approval_block_text", "Voor akkoord / oplevering");
    const introText = setting("intro_text", "");
    const footerText = setting("footer_text", "Werkbon automatisch gegenereerd vanuit StaffIQ.");

    const companyLines = [
      setting("company_address_1", invoiceSettings?.offer_company_address_1),
      setting("company_address_2", invoiceSettings?.offer_company_address_2),
      [
        setting("company_postcode", invoiceSettings?.offer_company_postcode),
        setting("company_city", invoiceSettings?.offer_company_city),
      ].filter(Boolean).join(" "),
      setting("company_country", invoiceSettings?.offer_company_country),
      setting("company_email", invoiceSettings?.offer_company_email),
      setting("company_website", invoiceSettings?.offer_company_website),
      setting("company_phone", invoiceSettings?.offer_company_phone),
      setting("company_kvk", invoiceSettings?.offer_company_kvk) ? `KvK: ${setting("company_kvk", invoiceSettings?.offer_company_kvk)}` : "",
      setting("company_vat", invoiceSettings?.offer_company_vat) ? `BTW: ${setting("company_vat", invoiceSettings?.offer_company_vat)}` : "",
      setting("company_iban", invoiceSettings?.offer_company_iban) ? `IBAN: ${setting("company_iban", invoiceSettings?.offer_company_iban)}` : "",
      setting("company_g_iban", invoiceSettings?.offer_company_g_iban || invoiceSettings?.company_g_iban) ? `G-rekening: ${setting("company_g_iban", invoiceSettings?.offer_company_g_iban || invoiceSettings?.company_g_iban)}` : "",
    ]
      .map((value) => safeText(value).trim())
      .filter(Boolean);

    const linesHtml = visibleLines.length
      ? visibleLines
          .map((line) => {
            const qty = Number(line.qty || line.quantity || 0);
            return `
              <tr>
                <td class="qty">${qty || ""}</td>
                <td>${escapeHtml(line.description || "")}</td>
              </tr>
            `;
          })
          .join("")
      : `<tr><td colspan="2">Geen opdrachtregels gevonden.</td></tr>`;

    const logoHtml = logo
      ? `<img src="${escapeHtml(logo)}" alt="Logo" class="logo" />`
      : "";

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(documentTitle)} ${escapeHtml(source?.offer_number || offerId || "")}</title>
          <style>
            @page { size: A4; margin: 12mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              font-family: Arial, Helvetica, sans-serif;
              color: #222;
              font-size: 12px;
              line-height: 1.4;
            }
            .top {
              display: flex;
              justify-content: space-between;
              gap: 30px;
              margin-bottom: 24px;
            }
            .logo { max-width: 190px; max-height: 85px; object-fit: contain; }
            .company { text-align: right; line-height: 1.5; }
            h1 { font-size: 23px; margin: 0 0 4px 0; }
            h2 {
              font-size: 15px;
              margin: 0 0 10px 0;
              padding-bottom: 5px;
              border-bottom: 2px solid #1f4e79;
              color: #1f4e79;
            }
            .meta {
              display: grid;
              grid-template-columns: 145px 1fr;
              gap: 5px 12px;
              margin-bottom: 22px;
            }
            .label { font-weight: 700; }
            .columns {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
              align-items: start;
            }
            .section {
              border: 1px solid #d9e0e7;
              border-radius: 7px;
              padding: 12px;
              margin-bottom: 16px;
              break-inside: avoid;
            }
            .row {
              display: grid;
              grid-template-columns: 155px 1fr;
              gap: 8px;
              padding: 4px 0;
              border-bottom: 1px solid #eee;
            }
            .row:last-child { border-bottom: none; }
            .text { white-space: pre-wrap; }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th {
              text-align: left;
              border-bottom: 2px solid #333;
              padding: 6px 4px;
            }
            td {
              vertical-align: top;
              border-bottom: 1px solid #ddd;
              padding: 7px 4px;
            }
            .qty {
              width: 60px;
              text-align: right;
            }
            .approval {
              margin-top: 24px;
              border: 1px solid #777;
              padding: 12px;
              min-height: 90px;
            }
            .footer {
              margin-top: 24px;
              padding-top: 8px;
              border-top: 1px solid #ddd;
              font-size: 10px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="top">
            <div>
              ${logoHtml}
              <h1>${escapeHtml(documentTitle)}</h1>
              <div>Opdracht: ${escapeHtml(source?.offer_number || offerId || "")}</div>
            </div>
            <div class="company">
              <strong>${escapeHtml(companyName)}</strong><br/>
              ${companyLines.map((line) => `${escapeHtml(line)}<br/>`).join("")}
            </div>
          </div>

          ${introText ? `<div style="margin:0 0 18px 0; white-space:pre-wrap;">${escapeHtml(introText)}</div>` : ""}

          <div class="meta">
            <div class="label">Relatie</div>
            <div>${escapeHtml(source?.organisation_name || "")}</div>

            <div class="label">Contactpersoon</div>
            <div>${escapeHtml(source?.contact_name || "Geen / niet van toepassing")}</div>

            <div class="label">Bedrijfscode</div>
            <div>${escapeHtml(source?.company_code_name || source?.company_code || "")}</div>

            <div class="label">Uitvoeringsdatum</div>
            <div>${escapeHtml(nlDate(form.execution_date))}</div>

            <div class="label">Aanvangstijd</div>
            <div>${escapeHtml(form.start_time || "")}</div>

            <div class="label">Uitgevoerd door</div>
            <div>${escapeHtml(form.executed_by || "")}</div>
          </div>

          <div class="columns">
            <div>
              <div class="section">
                <h2>${escapeHtml(executionTitle)}</h2>

                <div class="row">
                  <div class="label">Klant/toegang/sleutel</div>
                  <div class="text">${escapeHtml(form.customer_access || "")}</div>
                </div>

                <div class="row">
                  <div class="label">Sleutel retour</div>
                  <div>${yesNo(form.key_returned)}</div>
                </div>

                <div class="row">
                  <div class="label">Onderhoud/instructie</div>
                  <div class="text">${escapeHtml(form.maintenance_instruction || "")}</div>
                </div>

                <div class="row">
                  <div class="label">Foto's vereist</div>
                  <div>${yesNo(form.photos_required)}</div>
                </div>

                <div class="row">
                  <div class="label">Foto's ontvangen</div>
                  <div>${yesNo(form.photos_received)}</div>
                </div>
              </div>
            </div>

            <div>
              <div class="section">
                <h2>${escapeHtml(completionTitle)}</h2>

                <div class="row">
                  <div class="label">Status</div>
                  <div>${escapeHtml(statusLabel(form.completion_status))}</div>
                </div>

                <div class="row">
                  <div class="label">Opleverdatum</div>
                  <div>${escapeHtml(nlDate(form.completion_date))}</div>
                </div>

                <div class="row">
                  <div class="label">Akkoordgever</div>
                  <div>${escapeHtml(form.approved_name || "")}</div>
                </div>

                <div class="row">
                  <div class="label">Opmerking</div>
                  <div class="text">${escapeHtml(form.completion_notes || "")}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <h2>${escapeHtml(workTitle)}</h2>
            <table>
              <thead>
                <tr>
                  <th class="qty">Aantal</th>
                  <th>Omschrijving</th>
                </tr>
              </thead>
              <tbody>${linesHtml}</tbody>
            </table>
          </div>

          <div class="section">
            <h2>${escapeHtml(extraWorkTitle)}</h2>
            <div class="text">${escapeHtml(form.extra_work || "") || "-"}</div>
          </div>

          <div class="approval">
            <strong>${escapeHtml(approvalBlockText)}</strong><br/><br/>
            Naam: ${escapeHtml(form.approved_name || "")}<br/>
            Datum: ${escapeHtml(nlDate(form.completion_date))}<br/>
            Status: ${escapeHtml(statusLabel(form.completion_status))}
          </div>

          <div class="footer">
            ${escapeHtml(footerText)}
          </div>
        </body>
      </html>
    `;
  }

  function previewWorkOrder() {
    setError("");
    setPreviewOpen(true);
  }

  function closePreview() {
    if (saving || sendingEmail || sendingApproval) return;
    setPreviewOpen(false);
  }

  function printPreview() {
    const frame = document.getElementById("work-order-preview-frame");
    try {
      frame?.contentWindow?.focus?.();
      frame?.contentWindow?.print?.();
    } catch {
      setError("Printen van de werkbon is niet gelukt.");
    }
  }

  function relationPreference() {
    const email = safeText(source?.organisation_email).trim();
    const mobile = safeText(source?.organisation_mobile).trim();

    return {
      email,
      mobile,
      emailAllowed: source?.correspondence_email === true && !!email,
      whatsappAllowed: source?.correspondence_whatsapp === true && !!mobile,
    };
  }

  function normalizeWhatsappNumber(value) {
    let s = safeText(value).replace(/[^\d+]/g, "").replace(/^00/, "+");
    if (!s) return "";
    if (s.startsWith("+")) return s.replace(/[^\d]/g, "");
    if (s.startsWith("0")) return `31${s.slice(1)}`;
    if (s.startsWith("31")) return s;
    return `31${s}`;
  }

  function isMobileDeviceForWhatsapp() {
    try {
      return /Android|iPhone|iPad|iPod|Windows Phone/i.test(
        navigator.userAgent || ""
      );
    } catch {
      return false;
    }
  }

  function whatsappAppUrlFromWebUrl(webUrl) {
    const raw = safeText(webUrl).trim();
    if (!raw) return "";

    try {
      const parsed = new URL(raw);
      const host = safeText(parsed.hostname).toLowerCase();

      let phone = "";
      if (host.includes("wa.me")) {
        phone = parsed.pathname.replace(/^\/+/, "").split("/")[0] || "";
      } else {
        phone = parsed.searchParams.get("phone") || "";
      }

      const message = parsed.searchParams.get("text") || "";
      const params = new URLSearchParams();

      if (phone) params.set("phone", phone.replace(/[^\d]/g, ""));
      if (message) params.set("text", message);

      return `whatsapp://send${params.toString() ? `?${params.toString()}` : ""}`;
    } catch {
      return raw;
    }
  }

  function writeWhatsappReadyPage(targetWindow, webUrl) {
    if (!targetWindow || targetWindow.closed || !webUrl) return false;

    const appUrl = isMobileDeviceForWhatsapp()
      ? whatsappAppUrlFromWebUrl(webUrl)
      : webUrl;

    try {
      targetWindow.document.open();
      targetWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>WhatsApp openen</title>
          </head>
          <body style="font-family:Arial,sans-serif;padding:24px;line-height:1.45;color:#111;">
            <h2>WhatsApp staat klaar</h2>
            <p>Klik op de groene knop. Daarna moet je het bericht in WhatsApp nog zelf verzenden.</p>
            <p style="margin:24px 0;">
              <a
                href="${escapeHtml(appUrl)}"
                style="display:inline-block;background:#25D366;color:#111;text-decoration:none;padding:14px 18px;border-radius:8px;font-weight:700;"
              >
                Open WhatsApp
              </a>
            </p>
            <p style="font-size:13px;color:#555;">
              Werkt de knop niet?
              <a href="${escapeHtml(webUrl)}">Open via WhatsApp-web</a>
            </p>
          </body>
        </html>
      `);
      targetWindow.document.close();
      return true;
    } catch {
      return false;
    }
  }

  function resizePreviewFrame() {
    const frame = previewFrameRef.current;
    if (!frame) return;

    try {
      const doc = frame.contentDocument || frame.contentWindow?.document;
      if (!doc) return;

      doc.documentElement.style.overflow = "hidden";
      doc.body.style.overflow = "hidden";

      const height = Math.max(
        doc.body?.scrollHeight || 0,
        doc.documentElement?.scrollHeight || 0,
        900
      );

      frame.style.height = `${height + 8}px`;
    } catch {}
  }

  async function savePdfOnly() {
    const data = await fetchJson(
      `/api/offers/${encodeURIComponent(offerId)}/work-order`,
      {
        method: "POST",
        body: JSON.stringify(workOrderPayload()),
      }
    );

    const saved = data?.work_order || {};
    setForm((previous) => ({
      ...previous,
      ...saved,
      execution_date: isoDate(saved.execution_date || previous.execution_date),
      completion_date: isoDate(saved.completion_date || previous.completion_date),
      start_time: safeText(saved.start_time || previous.start_time),
    }));

    return await fetchJson(
      `/api/offers/${encodeURIComponent(offerId)}/work-order/save-document-html`,
      {
        method: "POST",
        body: JSON.stringify({
          html: buildWorkOrderHtml(),
        }),
      }
    );
  }

  async function emailWorkOrder() {
    const pref = relationPreference();

    if (!pref.email) {
      setError("Bij deze relatie staat geen e-mailadres.");
      return;
    }

    try {
      setSendingEmail(true);
      setError("");
      await savePdfOnly();

      const result = await fetchJson(
        `/api/offers/${encodeURIComponent(offerId)}/work-order/send-email`,
        {
          method: "POST",
          body: JSON.stringify({ to: pref.email }),
        }
      );

      const message = result?.message || `Werkbon verstuurd naar ${pref.email}`;
      const accepted = Array.isArray(result?.accepted)
        ? result.accepted.join(", ")
        : "";
      const rejected = Array.isArray(result?.rejected)
        ? result.rejected.join(", ")
        : "";

      alert(
        `${message}` +
          `${accepted ? `\nGeaccepteerd door mailserver: ${accepted}` : ""}` +
          `${rejected ? `\nNiet geaccepteerd: ${rejected}` : ""}`
      );

      setFlash(`${message} ✅`);
      window.setTimeout(() => setFlash(""), 2200);
    } catch (err) {
      setError(err?.message || "Werkbon e-mailen mislukt.");
    } finally {
      setSendingEmail(false);
    }
  }

  async function sendApprovalEmail(approvalId) {
    return await fetchJson(
      `/api/document-approvals/${encodeURIComponent(approvalId)}/send-email`,
      {
        method: "POST",
        body: JSON.stringify({}),
      }
    );
  }

  async function sendForApproval() {
    const pref = relationPreference();

    if (!pref.whatsappAllowed && !pref.emailAllowed) {
      setError("Bij deze relatie staat geen akkoord via WhatsApp of E-mail aangevinkt.");
      return;
    }

    let channel = pref.whatsappAllowed ? "whatsapp" : "email";

    if (pref.whatsappAllowed && pref.emailAllowed) {
      const choice = window.prompt(
        "Kies verzendwijze:\n1 = WhatsApp\n2 = E-mail\n3 = WhatsApp + E-mail",
        "1"
      );

      if (choice === null) return;

      const c = safeText(choice).trim();
      if (c === "1") channel = "whatsapp";
      else if (c === "2") channel = "email";
      else if (c === "3") channel = "both";
      else {
        setError("Ongeldige keuze. Gebruik 1, 2 of 3.");
        return;
      }
    }

    let whatsappWindow = null;
    if (channel === "whatsapp" || channel === "both") {
      whatsappWindow = window.open("", "_blank");
      if (whatsappWindow) {
        whatsappWindow.document.write(
          "<p style='font-family:Arial;padding:24px'>WhatsApp wordt voorbereid...</p>"
        );
      }
    }

    try {
      setSendingApproval(true);
      setError("");
      await savePdfOnly();

      const created = await fetchJson("/api/document-approvals", {
        method: "POST",
        body: JSON.stringify({
          document_type: "work_order",
          document_id: Number(offerId),
          channel: channel === "email" ? "email" : "whatsapp",
          approval_text:
            safeText(workOrderSettings?.approval_text).trim() ||
            `Ik ben bevoegd en geef akkoord op deze werkbon / oplevering ${source?.offer_number || offerId}.`,
          remarks: "",
        }),
      });

      const approval = created?.approval || {};
      const approvalId = approval?.id || created?.id;
      const whatsappUrl = created?.whatsapp_url || approval?.whatsapp_url || "";

      if (channel === "whatsapp" || channel === "both") {
        if (!whatsappUrl) {
          try { whatsappWindow?.close?.(); } catch {}
          if (channel === "whatsapp") {
            throw new Error("WhatsApp-link kon niet worden gemaakt.");
          }
        } else if (whatsappWindow) {
          const ready = writeWhatsappReadyPage(whatsappWindow, whatsappUrl);
          if (!ready) {
            whatsappWindow.location.href = whatsappUrl;
          }
        }
      }

      let emailResult = null;

      if (channel === "email" || channel === "both") {
        emailResult = await sendApprovalEmail(approvalId);

        const message =
          emailResult?.message ||
          `Akkoordverzoek verstuurd naar ${emailResult?.to || pref.email || ""}`;

        const accepted = Array.isArray(emailResult?.accepted)
          ? emailResult.accepted.join(", ")
          : "";

        const rejected = Array.isArray(emailResult?.rejected)
          ? emailResult.rejected.join(", ")
          : "";

        alert(
          `${message}` +
            `${accepted ? `\nGeaccepteerd door mailserver: ${accepted}` : ""}` +
            `${rejected ? `\nNiet geaccepteerd: ${rejected}` : ""}`
        );
      }

      setForm((previous) => ({
        ...previous,
        completion_status: "PENDING_APPROVAL",
      }));

      setFlash(
        channel === "both"
          ? "Werkbon via WhatsApp en e-mail ter akkoord aangeboden ✅"
          : channel === "email"
            ? "Werkbon per e-mail ter akkoord aangeboden ✅"
            : "Werkbon via WhatsApp ter akkoord aangeboden ✅"
      );

      window.setTimeout(() => setFlash(""), 2600);
    } catch (err) {
      try { whatsappWindow?.close?.(); } catch {}
      setError(err?.message || "Ter akkoord aanbieden mislukt.");
    } finally {
      setSendingApproval(false);
    }
  }

  function buildAiContext() {
    const lines = [
      `Opdracht: ${source?.offer_number || offerId || "-"}`,
      `Relatie: ${source?.organisation_name || "-"}`,
      `Contactpersoon: ${source?.contact_name || "-"}`,
      `Uitvoeringsdatum: ${form.execution_date || "-"}`,
      `Aanvangstijd: ${form.start_time || "-"}`,
      `Uitgevoerd door: ${form.executed_by || "-"}`,
      `Klant/toegang/sleutel: ${form.customer_access || "-"}`,
      `Sleutel retour: ${yesNo(form.key_returned)}`,
      `Onderhoud/instructie: ${form.maintenance_instruction || "-"}`,
      `Foto's vereist: ${yesNo(form.photos_required)}`,
      `Foto's ontvangen: ${yesNo(form.photos_received)}`,
      `Extra werkzaamheden: ${form.extra_work || "-"}`,
      `Opleverstatus: ${statusLabel(form.completion_status)}`,
      `Opleverdatum: ${form.completion_date || "-"}`,
      `Akkoordgever: ${form.approved_name || "-"}`,
      `Opleveropmerking: ${form.completion_notes || "-"}`,
      "",
      "OPDRACHTREGELS",
      ...visibleLines.map((line, index) =>
        `${index + 1}. ${Number(line.qty || line.quantity || 0) || ""} ${line.description || ""}`
      ),
    ];

    return lines.join("\n");
  }

  function openAiPopup() {
    setAiOpen(true);
    setAiText("");
    setFlash("");

    setTimeout(() => {
      try {
        aiTextRef.current?.focus?.({ preventScroll: true });
      } catch {
        try { aiTextRef.current?.focus?.(); } catch {}
      }
    }, 0);
  }

  function closeAiPopup() {
    setAiOpen(false);
    setAiText("");
  }

  function firstFilled(...values) {
    for (const value of values) {
      const textValue = safeText(value).trim();
      if (textValue) return textValue;
    }
    return "";
  }

  function runAiRelationData() {
    const lines = [];
    lines.push("RELATIE GEGEVENS");
    lines.push(`Naam: ${source?.organisation_name || "-"}`);
    lines.push(`Mobiel: ${source?.organisation_mobile || "-"}`);
    lines.push(`E-mail: ${source?.organisation_email || "-"}`);
    lines.push(`Contactpersoon: ${source?.contact_name || "Geen / niet van toepassing"}`);
    lines.push(`Bedrijfscode: ${source?.company_code_name || source?.company_code || "-"}`);

    setAiText(lines.join("\n"));
    setFlash("Relatiegegevens getoond ✓");
  }

  async function runAiWorkOrderCheck() {
    try {
      setAiBusy(true);
      setFlash("AI controleert werkbon...");

      const question = `
Controleer deze werkbon op:
1. ontbrekende gegevens;
2. onduidelijke of tegenstrijdige gegevens;
3. ontbrekende opleverinformatie;
4. praktische actiepunten.

Geef antwoord in duidelijk Nederlands met korte kopjes.
      `.trim();

      const result = await fetchJson("/api/ai/work-orders/chat", {
        method: "POST",
        body: JSON.stringify({
          question,
          context: buildAiContext(),
        }),
      });

      setAiText(result?.result || "Geen resultaat ontvangen.");
      setFlash("Werkbon gecontroleerd ✓");
    } catch (err) {
      setAiText(`FOUT:\n${err?.message || "Onbekende fout"}`);
      setFlash("");
    } finally {
      setAiBusy(false);
    }
  }

  async function runAiDossierOverview() {
    const organisationId = source?.organisation_id;

    if (!organisationId) {
      setAiText("Relatie-id ontbreekt. Dossieroverzicht kan niet worden geladen.");
      return;
    }

    try {
      setAiBusy(true);
      setFlash("Dossieroverzicht wordt gemaakt...");

      const dossier = await fetchJson(
        `/api/organisations/${encodeURIComponent(organisationId)}/dossier`
      );

      const organisation = dossier?.organisation || {};
      const contacts = Array.isArray(dossier?.contacts) ? dossier.contacts : [];
      const notesTasks = Array.isArray(dossier?.notes_tasks) ? dossier.notes_tasks : [];
      const offers = Array.isArray(dossier?.offers) ? dossier.offers : [];
      const invoices = Array.isArray(dossier?.invoices) ? dossier.invoices : [];

      const lines = [];
      lines.push("DOSSIER OVERZICHT");
      lines.push(`Relatie: ${firstFilled(organisation.name, source?.organisation_name) || "-"}`);
      lines.push(`Telefoon: ${firstFilled(organisation.phone, organisation.mobile) || "-"}`);
      lines.push(`E-mail: ${organisation.email || "-"}`);
      lines.push(`KvK: ${firstFilled(organisation.kvk, organisation.kvk_number) || "-"}`);
      lines.push("");

      lines.push(`CONTACTPERSONEN (${contacts.length})`);
      if (!contacts.length) lines.push("Geen contactpersonen gevonden.");
      contacts.slice(0, 20).forEach((contact) => {
        const name = firstFilled(
          contact.search_name,
          contact.full_name,
          contact.naam,
          [contact.first_name, contact.name_prefix, contact.last_name]
            .filter(Boolean)
            .join(" ")
        );
        lines.push(`- ${name || "-"} | ${firstFilled(contact.phone, contact.mobile) || "-"} | ${contact.email || "-"}`);
      });
      lines.push("");

      lines.push(`NOTITIES/TAKEN (${notesTasks.length})`);
      if (!notesTasks.length) lines.push("Geen notities/taken gevonden.");
      notesTasks.slice(0, 20).forEach((item) => {
        lines.push(
          `- ${firstFilled(item.kind, item.type) || "-"} | ` +
          `${firstFilled(item.subject, item.title) || "-"} | ` +
          `${item.status || "-"}`
        );
      });
      lines.push("");

      lines.push(`OFFERTES/OPDRACHTEN (${offers.length})`);
      if (!offers.length) lines.push("Geen offertes/opdrachten gevonden.");
      offers.slice(0, 20).forEach((item) => {
        lines.push(
          `- ${firstFilled(item.dossier_date, item.offer_date, item.invoice_date) || "-"} | ` +
          `${firstFilled(item.dossier_number, item.offer_number, item.invoice_number) || "-"} | ` +
          `${item.status || "-"}`
        );
      });
      lines.push("");

      lines.push(`FACTUREN (${invoices.length})`);
      if (!invoices.length) lines.push("Geen facturen gevonden.");
      invoices.slice(0, 20).forEach((item) => {
        lines.push(
          `- ${firstFilled(item.dossier_date, item.invoice_date) || "-"} | ` +
          `${firstFilled(item.dossier_number, item.invoice_number) || "-"} | ` +
          `${item.status || "-"}`
        );
      });

      setAiText(lines.join("\n"));
      setFlash("Dossieroverzicht gemaakt ✓");
    } catch (err) {
      setAiText(`FOUT:\n${err?.message || "Onbekende fout"}`);
      setFlash("");
    } finally {
      setAiBusy(false);
    }
  }

  function appendAiConversation(question, answer) {
    const block =
      `Vraag:\n${question || "-"}\n\n` +
      `----------------------------------------\n\n` +
      `Antwoord:\n${answer || "-"}\n\n` +
      `========================================\n\n`;

    setAiText((previous) => {
      const current = safeText(previous).trim();
      if (!current) return block;
      return `${current}\n\n${block}`;
    });
  }

  async function runAiChat() {
    const question = safeText(aiText).trim();

    if (!question) {
      setAiText(
        "Typ eerst een vraag voor Chat met AI.\n\n" +
        "Bijvoorbeeld:\nWat ontbreekt er nog in deze werkbon?"
      );

      setTimeout(() => {
        try {
          aiTextRef.current?.focus?.({ preventScroll: true });
        } catch {
          try { aiTextRef.current?.focus?.(); } catch {}
        }
      }, 0);
      return;
    }

    try {
      setAiBusy(true);
      setFlash("AI beantwoordt vraag...");

      const result = await fetchJson("/api/ai/work-orders/chat", {
        method: "POST",
        body: JSON.stringify({
          question,
          context: buildAiContext(),
        }),
      });

      appendAiConversation(question, result?.result || "Geen antwoord ontvangen.");
      setFlash("AI chat gereed ✓");
    } catch (err) {
      appendAiConversation(question, `FOUT: ${err?.message || "Onbekende fout"}`);
      setFlash("");
    } finally {
      setAiBusy(false);
    }
  }

  function printAiDocument() {
    const content = safeText(aiText).trim() || "Geen inhoud om te printen.";

    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>StaffIQ AI Werkbon / Oplevering</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 24px;
              color: #111;
              line-height: 1.45;
              white-space: pre-wrap;
            }
            h1 { font-size: 20px; margin: 0 0 18px 0; }
          </style>
        </head>
        <body>
          <h1>StaffIQ AI Werkbon / Oplevering</h1>
          <div>${escapeHtml(content)}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      try { printWindow.print(); } catch {}
    }, 300);
  }

  const aiVoice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 8500,
    onCommand: () => false,
    onDictation: (spokenText) => {
      const spoken = safeText(spokenText).trim();
      if (!spoken) return;

      setAiText((previous) => {
        const current = safeText(previous);
        const separator =
          current && !current.endsWith(" ") && !current.endsWith("\n")
            ? " "
            : "";
        return `${current}${separator}${spoken}`;
      });
    },
  });

  function startAiSpeak() {
    if (!aiVoice.supported || aiVoice.listening) return;

    try {
      aiTextRef.current?.focus?.({ preventScroll: true });
    } catch {
      try { aiTextRef.current?.focus?.(); } catch {}
    }

    aiVoice.startOnce();
  }


  async function saveWorkOrder(options = {}) {
    if (isReadonly || saving) return;

    const createDocument = options.createDocument === true;
    const returnToOrder = options.returnToOrder !== false;

    try {
      setSaving(true);
      setError("");
      setFlash("");

      const data = await fetchJson(
        `/api/offers/${encodeURIComponent(offerId)}/work-order`,
        {
          method: "POST",
          body: JSON.stringify(workOrderPayload()),
        }
      );

      const saved = data?.work_order || {};
      setForm((previous) => ({
        ...previous,
        ...saved,
        execution_date: isoDate(saved.execution_date || previous.execution_date),
        completion_date: isoDate(saved.completion_date || previous.completion_date),
        start_time: safeText(saved.start_time || previous.start_time),
      }));

      if (createDocument) {
        const documentResult = await fetchJson(
          `/api/offers/${encodeURIComponent(offerId)}/work-order/save-document-html`,
          {
            method: "POST",
            body: JSON.stringify({
              html: buildWorkOrderHtml(),
            }),
          }
        );

        setFlash(
          `Werkbon opgeslagen in Documenten: ${documentResult?.file_name || "PDF"} ✅`
        );
      } else {
        setFlash("Werkbon / Oplevering opgeslagen ✅");
      }

      if (returnToOrder) {
        try {
          sessionStorage.setItem("staffiq_reopen_offer_id", String(offerId || ""));
        } catch {}

        window.setTimeout(() => {
          navigate("/offertes");
        }, createDocument ? 1100 : 650);
      }
    } catch (err) {
      setError(err?.message || "Werkbon opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }


  function PreviewButtons() {
    const pref = relationPreference();
    const busy = saving || sendingEmail || sendingApproval;

    const base = {
      padding: "8px 12px",
      border: "none",
      borderRadius: 4,
      fontWeight: 700,
      cursor: busy ? "not-allowed" : "pointer",
      color: "white",
      opacity: busy ? 0.55 : 1,
    };

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={openAiPopup}
          disabled={busy}
          style={{ ...base, background: "#6f42c1" }}
        >
          ✨ AI
        </button>

        <button
          type="button"
          onClick={printPreview}
          disabled={busy}
          style={{ ...base, background: "#6c757d" }}
        >
          Printen
        </button>

        <button
          type="button"
          onClick={emailWorkOrder}
          disabled={busy || !pref.email}
          style={{
            ...base,
            background: "#198754",
            opacity: busy || !pref.email ? 0.5 : 1,
            cursor: busy || !pref.email ? "not-allowed" : "pointer",
          }}
        >
          {sendingEmail ? "Bezig..." : "E-mailen"}
        </button>

        <button
          type="button"
          onClick={sendForApproval}
          disabled={busy || (!pref.emailAllowed && !pref.whatsappAllowed)}
          style={{
            ...base,
            background: "#0d6efd",
            opacity:
              busy || (!pref.emailAllowed && !pref.whatsappAllowed) ? 0.5 : 1,
            cursor:
              busy || (!pref.emailAllowed && !pref.whatsappAllowed)
                ? "not-allowed"
                : "pointer",
          }}
        >
          {sendingApproval ? "Bezig..." : "Ter akkoord"}
        </button>

        <button
          type="button"
          onClick={closePreview}
          disabled={busy}
          style={{ ...base, background: "#2f7dd1" }}
        >
          Sluiten
        </button>
      </div>
    );
  }


  function FormActionButtons({ top = false }) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          flexWrap: "wrap",
          marginTop: top ? 0 : 16,
        }}
      >
        <button
          type="button"
          onClick={() => navigate("/offertes")}
          style={{
            ...buttonStyle,
            background: "white",
            color: "#333",
            border: "1px solid #ccc",
          }}
        >
          Annuleren / Terug
        </button>

        <button
          type="button"
          onClick={previewWorkOrder}
          disabled={saving}
          style={{
            ...buttonStyle,
            background: "#6c757d",
            color: "white",
            opacity: saving ? 0.55 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          Voortgang werkbon
        </button>

        <button
          type="button"
          onClick={() => saveWorkOrder({ createDocument: false })}
          disabled={isReadonly || saving}
          style={{
            ...buttonStyle,
            background: "#2f7dd1",
            color: "white",
            opacity: isReadonly || saving ? 0.55 : 1,
            cursor: isReadonly || saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Opslaan…" : "Opslaan"}
        </button>

        <button
          type="button"
          onClick={() => saveWorkOrder({ createDocument: true })}
          disabled={isReadonly || saving}
          style={{
            ...buttonStyle,
            background: "#198754",
            color: "white",
            opacity: isReadonly || saving ? 0.55 : 1,
            cursor: isReadonly || saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Bezig…" : "Opslaan + PDF"}
        </button>
      </div>
    );
  }


  const inputStyle = {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "8px 10px",
    border: "1px solid #d6dce3",
    borderRadius: 5,
    background: "white",
    font: "inherit",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: 88,
    resize: "vertical",
    lineHeight: 1.4,
  };

  const labelStyle = {
    display: "grid",
    gap: 5,
    fontSize: 13,
    color: "#333",
  };

  const buttonStyle = {
    padding: "9px 14px",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontWeight: 700,
  };

  if (loading) {
    return <div>Werkbon laden…</div>;
  }

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          <h1 style={{ margin: 0 }}>
            Werkbon / Oplevering{" "}
            <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
          </h1>

          <HelpButton
            topic="werkbonnen"
            title="Help over Werkbon / Oplevering"
          />
        </div>

        <FormActionButtons top />
      </div>

      {error && (
        <div style={{ marginBottom: 12, color: "crimson" }}>{error}</div>
      )}

      {flash && (
        <div style={{ marginBottom: 12, color: "green" }}>{flash}</div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 10,
          background: "white",
          border: "1px solid #e0e5ea",
          borderRadius: 9,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <label style={labelStyle}>
          Opdracht
          <input
            value={source?.offer_number || offerId || ""}
            readOnly
            disabled
            style={{ ...inputStyle, background: "#f4f4f4" }}
          />
        </label>

        <label style={labelStyle}>
          Relatie
          <input
            value={source?.organisation_name || ""}
            readOnly
            disabled
            style={{ ...inputStyle, background: "#f4f4f4" }}
          />
        </label>

        <label style={labelStyle}>
          Contactpersoon
          <input
            value={source?.contact_name || "(geen / niet van toepassing)"}
            readOnly
            disabled
            style={{ ...inputStyle, background: "#f4f4f4" }}
          />
        </label>

        <label style={labelStyle}>
          Bedrijfscode
          <input
            value={source?.company_code_name || source?.company_code || ""}
            readOnly
            disabled
            style={{ ...inputStyle, background: "#f4f4f4" }}
          />
        </label>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 14,
          alignItems: "start",
        }}
      >
        <section
          style={{
            background: "white",
            border: "1px solid #e0e5ea",
            borderRadius: 9,
            padding: 14,
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: 18 }}>Uitvoering</h2>

          <div style={{ display: "grid", gap: 11 }}>
            <label style={labelStyle}>
              Uitvoeringsdatum
              <input
                type="date"
                value={form.execution_date || ""}
                onChange={(event) => updateField("execution_date", event.target.value)}
                disabled={isReadonly}
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Aanvangstijd
              <input
                type="time"
                value={form.start_time || ""}
                onChange={(event) => updateField("start_time", event.target.value)}
                disabled={isReadonly}
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Uitgevoerd door
              <input
                value={form.executed_by || ""}
                onChange={(event) => updateField("executed_by", event.target.value)}
                disabled={isReadonly}
                placeholder="Naam medewerker, ploeg of onderaannemer"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Klant aanwezig / toegang / sleutel
              <textarea
                value={form.customer_access || ""}
                onChange={(event) => updateField("customer_access", event.target.value)}
                disabled={isReadonly}
                style={textareaStyle}
              />
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={form.key_returned === true}
                onChange={(event) => updateField("key_returned", event.target.checked)}
                disabled={isReadonly}
              />
              Sleutel retour
            </label>

            <label style={labelStyle}>
              Onderhoudsmiddel / instructie
              <textarea
                value={form.maintenance_instruction || ""}
                onChange={(event) => updateField("maintenance_instruction", event.target.value)}
                disabled={isReadonly}
                style={textareaStyle}
              />
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={form.photos_required === true}
                onChange={(event) => updateField("photos_required", event.target.checked)}
                disabled={isReadonly}
              />
              Foto's vereist
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={form.photos_received === true}
                onChange={(event) => updateField("photos_received", event.target.checked)}
                disabled={isReadonly}
              />
              Foto's ontvangen
            </label>
          </div>
        </section>

        <section
          style={{
            background: "white",
            border: "1px solid #e0e5ea",
            borderRadius: 9,
            padding: 14,
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: 18 }}>Werkzaamheden</h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "58px minmax(0, 1fr) 100px",
              gap: 10,
              borderBottom: "2px solid #444",
              paddingBottom: 7,
              marginBottom: 2,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <div style={{ textAlign: "right" }}>Aantal</div>
            <div>Omschrijving</div>
            <div></div>
          </div>

          {workLines.length ? (
            <div>
              {workLines.map((line, index) => {
                const isEditing = editingWorkLineIndex === index;
                const isDragging = draggingWorkLineIndex === index;
                const isDragOver = dragOverWorkLineIndex === index;

                if (isEditing) {
                  return (
                    <div
                      key={line.id || index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "58px minmax(0, 1fr) 100px",
                        gap: 10,
                        alignItems: "start",
                        padding: "8px 0",
                        borderBottom: "1px solid #ddd",
                        background: "#f5f9ff",
                      }}
                    >
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={workLineDraft.qty ?? ""}
                        onChange={(event) =>
                          setWorkLineDraft((previous) => ({
                            ...previous,
                            qty: event.target.value,
                          }))
                        }
                        onFocus={(event) => event.target.select()}
                        disabled={isReadonly}
                        style={{
                          ...inputStyle,
                          textAlign: "right",
                          padding: "7px 6px",
                        }}
                      />

                      <textarea
                        value={workLineDraft.description || ""}
                        onChange={(event) =>
                          setWorkLineDraft((previous) => ({
                            ...previous,
                            description: event.target.value,
                          }))
                        }
                        disabled={isReadonly}
                        autoFocus
                        placeholder="Omschrijving werkzaamheden"
                        style={{
                          ...inputStyle,
                          minHeight: 64,
                          resize: "vertical",
                          lineHeight: 1.4,
                        }}
                      />

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 5,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          onClick={saveEditedWorkLine}
                          title="Opslaan"
                          style={{
                            border: "1px solid #cfd8e3",
                            background: "#2f7dd1",
                            color: "#fff",
                            cursor: "pointer",
                            fontSize: 12,
                            borderRadius: 6,
                            padding: "4px 7px",
                          }}
                        >
                          Opslaan
                        </button>

                        <button
                          type="button"
                          onClick={cancelEditWorkLine}
                          title="Annuleren"
                          style={{
                            border: "1px solid #cfd8e3",
                            background: "#f4f4f4",
                            color: "#333",
                            cursor: "pointer",
                            fontSize: 12,
                            borderRadius: 6,
                            padding: "4px 7px",
                          }}
                        >
                          Annuleren
                        </button>
                      </div>
                    </div>
                  );
                }

                const iconButtonStyle = {
                  border: "none",
                  background: "transparent",
                  cursor: isReadonly ? "not-allowed" : "pointer",
                  fontSize: 14,
                  opacity: isReadonly ? 0.25 : 0.75,
                  padding: 0,
                  margin: 0,
                  lineHeight: 1,
                };

                return (
                  <div
                    key={line.id || index}
                    draggable={!isReadonly}
                    onDragStart={(event) => {
                      if (isReadonly) return;
                      setDraggingWorkLineIndex(index);
                      try {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", String(index));
                      } catch {}
                    }}
                    onDragOver={(event) => {
                      if (isReadonly) return;
                      event.preventDefault();
                      setDragOverWorkLineIndex(index);
                    }}
                    onDragLeave={() => {
                      setDragOverWorkLineIndex((current) =>
                        current === index ? null : current
                      );
                    }}
                    onDrop={(event) => {
                      if (isReadonly) return;
                      event.preventDefault();

                      let fromIndex = draggingWorkLineIndex;
                      try {
                        const transferred = Number(
                          event.dataTransfer.getData("text/plain")
                        );
                        if (Number.isInteger(transferred)) {
                          fromIndex = transferred;
                        }
                      } catch {}

                      reorderWorkLine(fromIndex, index);
                    }}
                    onDragEnd={() => {
                      setDraggingWorkLineIndex(null);
                      setDragOverWorkLineIndex(null);
                    }}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "58px minmax(0, 1fr) 100px",
                      gap: 10,
                      alignItems: "start",
                      padding: "8px 0",
                      borderBottom: "1px solid #ddd",
                      borderTop: isDragOver
                        ? "3px solid #2f7dd1"
                        : "none",
                      background: isDragging ? "#eef6ff" : "transparent",
                      fontSize: 14,
                      lineHeight: 1.45,
                      cursor: isReadonly ? "default" : "move",
                    }}
                  >
                    <div
                      onClick={() => !isReadonly && startEditWorkLine(index)}
                      style={{
                        textAlign: "right",
                        cursor: isReadonly ? "default" : "pointer",
                      }}
                    >
                      {Number(line.qty || 0) || ""}
                    </div>

                    <div
                      onClick={() => !isReadonly && startEditWorkLine(index)}
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        cursor: isReadonly ? "default" : "text",
                      }}
                    >
                      {line.description || ""}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 5,
                        flexWrap: "nowrap",
                        minHeight: 24,
                      }}
                    >
                      <span
                        title="Sleep deze regel naar boven of beneden"
                        style={{
                          ...iconButtonStyle,
                          cursor: isReadonly ? "default" : "grab",
                          userSelect: "none",
                        }}
                      >
                        ↕
                      </span>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          moveWorkLineStep(index, -1);
                        }}
                        disabled={isReadonly || index === 0}
                        title="Regel omhoog"
                        style={{
                          ...iconButtonStyle,
                          opacity:
                            !isReadonly && index > 0 ? 0.75 : 0.25,
                          cursor:
                            !isReadonly && index > 0
                              ? "pointer"
                              : "not-allowed",
                        }}
                      >
                        ↑
                      </button>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          moveWorkLineStep(index, 1);
                        }}
                        disabled={
                          isReadonly || index >= workLines.length - 1
                        }
                        title="Regel omlaag"
                        style={{
                          ...iconButtonStyle,
                          opacity:
                            !isReadonly && index < workLines.length - 1
                              ? 0.75
                              : 0.25,
                          cursor:
                            !isReadonly && index < workLines.length - 1
                              ? "pointer"
                              : "not-allowed",
                        }}
                      >
                        ↓
                      </button>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          startEditWorkLine(index);
                        }}
                        disabled={isReadonly}
                        title="Bewerken"
                        style={iconButtonStyle}
                      >
                        ✏️
                      </button>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          removeWorkLine(index);
                        }}
                        disabled={isReadonly}
                        title="Verwijderen"
                        style={iconButtonStyle}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: "12px 0", color: "#777", fontSize: 14 }}>
              (nog geen werkzaamheden)
            </div>
          )}

          {!isReadonly && editingWorkLineIndex === null && (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 12,
              }}
            >
              <button
                type="button"
                onClick={addWorkLine}
                style={{
                  padding: "8px 14px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "white",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Handmatig toevoegen
              </button>

              <button
                type="button"
                onClick={resetWorkLinesFromOffer}
                style={{
                  padding: "8px 14px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "white",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Herstel uit opdracht
              </button>
            </div>
          )}

          <label style={{ ...labelStyle, marginTop: 16 }}>
            Extra materialen / werkzaamheden
            <textarea
              value={form.extra_work || ""}
              onChange={(event) => updateField("extra_work", event.target.value)}
              disabled={isReadonly}
              style={{ ...textareaStyle, minHeight: 130 }}
            />
          </label>
        </section>

        <section
          style={{
            background: "white",
            border: "1px solid #e0e5ea",
            borderRadius: 9,
            padding: 14,
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: 18 }}>Oplevering</h2>

          <div style={{ display: "grid", gap: 11 }}>
            <label style={labelStyle}>
              Opleveringsstatus
              <select
                value={form.completion_status || "NOT_COMPLETED"}
                onChange={(event) => updateField("completion_status", event.target.value)}
                disabled={isReadonly}
                style={inputStyle}
              >
                <option value="NOT_COMPLETED">Niet opgeleverd</option>
                <option value="PENDING_APPROVAL">Ter akkoord</option>
                <option value="COMPLETED">Opgeleverd</option>
              </select>
            </label>

            <label style={labelStyle}>
              Opleverdatum
              <input
                type="date"
                value={form.completion_date || ""}
                onChange={(event) => updateField("completion_date", event.target.value)}
                disabled={isReadonly}
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Naam akkoordgever
              <input
                value={form.approved_name || ""}
                onChange={(event) => updateField("approved_name", event.target.value)}
                disabled={isReadonly}
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Opleveropmerking
              <textarea
                value={form.completion_notes || ""}
                onChange={(event) => updateField("completion_notes", event.target.value)}
                disabled={isReadonly}
                style={{ ...textareaStyle, minHeight: 130 }}
              />
            </label>

            <div
              style={{
                padding: 10,
                background: "#f5f8fb",
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              Huidige status: <b>{statusLabel(form.completion_status)}</b>
            </div>
          </div>
        </section>
      </div>

      <FormActionButtons />

      {previewOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99990,
            background: "rgba(0,0,0,0.45)",
            padding: 18,
            boxSizing: "border-box",
            overflow: "auto",
          }}
        >
          <div
            style={{
              width: "min(1120px, 100%)",
              margin: "0 auto",
              background: "white",
              borderRadius: 10,
              padding: 18,
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            }}
          >
            <PreviewButtons />

            <iframe
              ref={previewFrameRef}
              id="work-order-preview-frame"
              title="Voortgang werkbon"
              srcDoc={buildWorkOrderHtml()}
              scrolling="no"
              onLoad={resizePreviewFrame}
              style={{
                width: "100%",
                height: "1050px",
                overflow: "hidden",
                border: "1px solid #ddd",
                marginTop: 14,
                marginBottom: 14,
                background: "white",
              }}
            />

            <PreviewButtons />
          </div>
        </div>
      )}

      {aiOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 100000,
            padding: window.innerWidth <= 768 ? 10 : 24,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: window.innerWidth <= 768 ? "100%" : 900,
              height: window.innerWidth <= 768 ? "92vh" : "82vh",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 20px 70px rgba(0,0,0,0.30)",
              padding: 18,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              <h2 style={{ margin: 0 }}>AI Werkbon / Oplevering</h2>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={printAiDocument}
                  disabled={aiBusy}
                  style={{
                    ...buttonStyle,
                    background: "white",
                    border: "1px solid #d9d9d9",
                    opacity: aiBusy ? 0.55 : 1,
                    cursor: aiBusy ? "not-allowed" : "pointer",
                  }}
                >
                  Printen
                </button>

                <button
                  type="button"
                  onClick={startAiSpeak}
                  disabled={!aiVoice.supported || aiVoice.listening || aiBusy}
                  style={{
                    ...buttonStyle,
                    background: "white",
                    border: "1px solid #d9d9d9",
                    opacity:
                      !aiVoice.supported || aiVoice.listening || aiBusy
                        ? 0.55
                        : 1,
                    cursor:
                      !aiVoice.supported || aiVoice.listening || aiBusy
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {aiVoice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
                </button>

                <button
                  type="button"
                  onClick={closeAiPopup}
                  style={{
                    ...buttonStyle,
                    background: "white",
                    border: "1px solid #d9d9d9",
                  }}
                >
                  Sluiten
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 10, fontWeight: 700 }}>
              Kies een actie:
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth <= 768
                    ? "1fr"
                    : "1fr 1fr 1fr 1fr",
                gap: 8,
                marginBottom: 14,
              }}
            >
              <button
                type="button"
                onClick={runAiRelationData}
                disabled={aiBusy}
                style={{
                  ...buttonStyle,
                  background: "white",
                  border: "1px solid #d9d9d9",
                  opacity: aiBusy ? 0.55 : 1,
                }}
              >
                Relatie gegevens
              </button>

              <button
                type="button"
                onClick={runAiWorkOrderCheck}
                disabled={aiBusy}
                style={{
                  ...buttonStyle,
                  background: "white",
                  border: "1px solid #d9d9d9",
                  opacity: aiBusy ? 0.55 : 1,
                }}
              >
                Werkbon controleren
              </button>

              <button
                type="button"
                onClick={runAiDossierOverview}
                disabled={aiBusy}
                style={{
                  ...buttonStyle,
                  background: "white",
                  border: "1px solid #d9d9d9",
                  opacity: aiBusy ? 0.55 : 1,
                }}
              >
                Dossier overzicht
              </button>

              <button
                type="button"
                onClick={runAiChat}
                disabled={aiBusy}
                style={{
                  ...buttonStyle,
                  background: "white",
                  border: "1px solid #d9d9d9",
                  opacity: aiBusy ? 0.55 : 1,
                }}
              >
                Chat met AI
              </button>
            </div>

            <div style={{ marginBottom: 8, fontWeight: 700 }}>
              AI venster
            </div>

            <textarea
              ref={aiTextRef}
              value={aiText}
              onChange={(event) => setAiText(event.target.value)}
              placeholder="Klik op een AI-keuze, of typ hier je vraag voor Chat met AI."
              style={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                padding: 12,
                border: "1px solid #ccc",
                borderRadius: 8,
                fontFamily: "inherit",
                fontSize: 15,
                lineHeight: 1.45,
                boxSizing: "border-box",
                resize: "none",
                whiteSpace: "pre-wrap",
                overflowY: "auto",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
